// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.

import React from 'react';
import { createRoot } from 'react-dom/client';
import EditorHeader, {
    EditorHeaderType,
} from '../components/design/card/EditorHeader';
import * as Adaptive from 'adaptivecards';
import * as monaco from 'monaco-editor';
import * as Constants from './Constants';
import CardDesignerSurface from './CardDesignerSurface';
import HostContainer from './HostContainer';
import AdaptiveCardSchema from './AdaptiveCardSchema';
import { IPoint } from './Miscellaneous';
import BaseInventoryItem from './BaseInventoryItem';
import * as Shared from './Shared';
import TreeViewSurface from './TreeViewSurface';
import DraggableElement from './DraggableElement';
import Splitter from './Splitter';
import DesignerPeer from './DesignerPeers/DesignerPeer';
import { AdaptiveCard, Version } from 'adaptivecards';
import { renderErrorPane } from './CardDesignerUtils';
import { AWTLogManager, AWTEventPriority } from '@aria/webjs-sdk';
import {
    DRAG_DROP,
    JSON_EDITOR,
    JSON_COPIED,
} from '../utilities/LoggingEventNames';
import { CardDesignerEditor } from './CardDesignerEditor';

export default class CardDesigner {
    private static internalProcessMarkdown(
        text: string,
        result: Adaptive.IMarkdownProcessingResult,
    ) {
        if (CardDesigner.onProcessMarkdown) {
            CardDesigner.onProcessMarkdown(text, result);
        } else {
            // Check for markdownit
            if (window['markdownit']) {
                result.outputHtml = window['markdownit']().render(text);
                result.didProcess = true;
            }
        }
    }

    static onProcessMarkdown: (
        text: string,
        result: Adaptive.IMarkdownProcessingResult,
    ) => void = null;

    private static MAX_UNDO_STACK_SIZE = 50;

    private _cardEditor: monaco.editor.IStandaloneCodeEditor;
    private _hostContainer: HostContainer;
    private _isMonacoEditorLoaded: boolean = false;
    private _isJsonEditorVisible: boolean = false;
    private _designerSurface: CardDesignerSurface;
    private _designerHostElement: HTMLElement;
    private _draggedInventoryItem: BaseInventoryItem;
    private _draggedElement: HTMLElement;
    private _currentMousePosition: IPoint;
    private _card: AdaptiveCard;
    private _undoStack: Array<string> = [];
    private _undoStackIndex: number = -1;
    private _propertySheetElement: HTMLElement;
    private _jsonEditorElement: HTMLElement;
    private _jsonEditorSize: string = '50%';
    private _jsonEditorHeaderElement: HTMLElement;
    private _editorElement: HTMLElement;
    private _treeViewSurface: TreeViewSurface;

    private createEmptyDiv(): HTMLElement {
        let container: HTMLElement = document.createElement('div');
        container.classList.add('noPropertySelectedPropertyEditor');
        container.innerHTML = 'Select an element to customize its properties';
        return container;
    }

    private buildPropertySheet(peer: DesignerPeer) {
        if (this._propertySheetElement) {
            this._propertySheetElement.innerHTML = '';
            let container: HTMLElement;
            const propertyEditorHeaderType = document.getElementById(
                'propertyEditorHeaderType',
            );
            propertyEditorHeaderType.innerHTML = '';

            if (peer) {
                propertyEditorHeaderType.classList.remove('acd-hidden');

                let peerName: HTMLElement = document.createElement('span');
                peerName.textContent = peer.getCardObject().getJsonTypeName();
                propertyEditorHeaderType.appendChild(peerName);

                let removePeerIcon: HTMLElement =
                    document.createElement('span');
                removePeerIcon.classList.add(
                    'amd-icon',
                    'amd-blue-icon',
                    'editorHelpIcon',
                    'amd-icon-garbage',
                );

                removePeerIcon.setAttribute('aria-label', 'Remove');
                removePeerIcon.setAttribute('tabIndex', '0');
                removePeerIcon.setAttribute('role', 'button');

                removePeerIcon.onclick = () => {
                    this._designerSurface.removeSelected();
                };
                propertyEditorHeaderType.appendChild(removePeerIcon);

                container = peer.buildPropertySheet(this.currentTargetVersion);
                container.style.paddingBottom = '8px';
                this._propertySheetElement.appendChild(container);
            } else {
                propertyEditorHeaderType.classList.add('acd-hidden');
                container = this.createEmptyDiv();
                this._propertySheetElement.appendChild(container);
            }
        }
    }

    public addInventoryItem(inventoryItem: BaseInventoryItem) {
        inventoryItem.render();
        inventoryItem.onStartDrag = (sender: DraggableElement) => {
            this._draggedInventoryItem = sender as BaseInventoryItem;

            this._draggedElement =
                this._draggedInventoryItem.renderDragVisual();
            this._draggedElement.style.position = 'absolute';
            this._draggedElement.style.left =
                this._currentMousePosition.x + 'px';
            this._draggedElement.style.top =
                this._currentMousePosition.y + 'px';
            this._draggedElement.style.zIndex = '1000';

            document.body.appendChild(this._draggedElement);
        };
    }

    private endDrag() {
        if (this._draggedInventoryItem) {
            AWTLogManager.getLogger().logEvent({
                name: DRAG_DROP,
                priority: AWTEventPriority.Normal,
            });
            this._draggedInventoryItem.endDrag();
            this._draggedElement.remove();

            this._draggedInventoryItem = null;
            this._draggedElement = null;
        }
    }

    private ensureVisible(
        element: HTMLElement,
        scrollingParent: HTMLElement,
        reduceParentBottom: number = 0,
    ) {
        const parentBBox = scrollingParent.getBoundingClientRect();
        const elementBBox = element.getBoundingClientRect();
        let scrollBy = 0;

        if (elementBBox.height <= parentBBox.height - reduceParentBottom) {
            // Incase, the element height is not greater than the parent
            if (elementBBox.top < parentBBox.top) {
                scrollBy = -(parentBBox.top - elementBBox.top);
            } else if (
                elementBBox.bottom >
                parentBBox.bottom - reduceParentBottom
            ) {
                scrollBy =
                    elementBBox.bottom -
                    (parentBBox.bottom - reduceParentBottom);
            }
        } else {
            scrollBy = -(parentBBox.top - elementBBox.top);
        }
        scrollingParent.scrollTop += scrollBy;
    }

    private onPeerSelected(peer: DesignerPeer) {
        const editorCanvas = document.getElementById('editorCanvas');
        this.ensureVisible(
            peer.renderedElement,
            editorCanvas,
            this._jsonEditorHeaderElement.getBoundingClientRect().height,
        );
        this.ensureVisible(
            peer.treeItem.renderedElement,
            this._treeViewSurface.treeViewElement,
        );
    }

    private recreateDesignerSurface() {
        this._hostContainer.initialize();

        this._designerHostElement.innerHTML = '';
        this._hostContainer.renderTo(this._designerHostElement);

        this._designerSurface = new CardDesignerSurface(
            this._hostContainer.cardHost,
            this.onPeerSelected.bind(this),
        );
        this._designerSurface.fixedHeightCard =
            this._hostContainer.isFixedHeight;
        this._designerSurface.designerSurfaceElement.addEventListener(
            'keydown',
            (e: KeyboardEvent) => {
                switch (e.key) {
                    case 'z':
                        if (e.ctrlKey || e.metaKey) {
                            if (e.shiftKey) {
                                this.redo();
                            } else {
                                this.undo();
                            }
                        }
                        break;
                    case 'y':
                        if (e.ctrlKey || e.metaKey) {
                            this.redo();
                        }
                        break;
                }
            },
        );
        this._designerSurface.onSelectedPeerChanged = (peer: DesignerPeer) => {
            this.buildPropertySheet(peer);
        };
        this._designerSurface.onLayoutUpdated = (isFullRefresh: boolean) => {
            if (isFullRefresh) {
                this.scheduleUpdateJsonFromCard();
            }

            this._treeViewSurface.buildTreeView(this._designerSurface);
        };

        this._designerSurface.onCardValidated = (
            parseErrors: Array<Adaptive.IValidationError>,
            validationResults: Adaptive.ValidationResults,
            syntaxErrors: string,
        ) => {
            const errorPane = document.getElementById('errorPane');
            if (syntaxErrors) {
                renderErrorPane(true, syntaxErrors);
                errorPane.style.cursor = 'pointer';
                errorPane.onclick = () => {
                    this.isJsonEditorVisible = true;
                    this.navigateToErrorLine();
                };
            } else if (parseErrors.length > 0) {
                let errorMessages: Array<string> = [];
                for (let error of parseErrors) {
                    if (errorMessages.indexOf(error.message) < 0) {
                        errorMessages.push(error.message);
                    }
                }
                renderErrorPane(true, errorMessages[0]);
                errorPane.style.cursor = 'default';
                errorPane.onclick = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                };
            } else if (validationResults.failures.length > 0) {
                renderErrorPane(
                    true,
                    '[' +
                        validationResults.failures[0].cardObject.getJsonTypeName() +
                        ']' +
                        validationResults.failures[0].errors[0].message,
                );
                errorPane.style.cursor = 'default';
                errorPane.onclick = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                };
            } else {
                renderErrorPane(false, '');
            }

            if (
                !Shared.isVersionLessOrEqual(
                    this._card.version,
                    new Version(1, 3),
                )
            ) {
                renderErrorPane(false, '');
            }
        };

        this.buildPropertySheet(null);

        if (this._card) {
            this._card.hostConfig = this._hostContainer.getHostConfig();
        }

        this._designerSurface.card = this._card;
    }

    public updateJsonEditorsLayout() {
        if (this._isMonacoEditorLoaded) {
            this._cardEditor.layout();
        }
    }

    private jsonUpdateTimer: any;
    private cardUpdateTimer: any;
    private updateLayoutTimer: any;

    private preventCardUpdate: boolean = false;

    private setCardPayload(payload: object) {
        if (this._isMonacoEditorLoaded) {
            this._cardEditor.setValue(JSON.stringify(payload, null, 4));
        }
    }

    private updateJsonFromCard(addToUndoStack: boolean = true) {
        try {
            this.preventCardUpdate = true;

            if (
                !this.preventJsonUpdate &&
                !this._designerSurface.preventJsonUpdate &&
                this._isMonacoEditorLoaded
            ) {
                let cardPayload = this.card.toJSON();

                if (addToUndoStack) {
                    this.addToUndoStack(JSON.stringify(cardPayload));
                }

                this.setCardPayload(cardPayload);
            }
        } finally {
            this.preventCardUpdate = false;
        }
    }

    private scheduleUpdateJsonFromCard() {
        clearTimeout(this.jsonUpdateTimer);

        if (!this.preventJsonUpdate) {
            this.jsonUpdateTimer = setTimeout(() => {
                this.updateJsonFromCard();
            }, 100);
        }
    }

    private preventJsonUpdate: boolean = false;

    public getCurrentCardEditorPayload(): string {
        return this._isMonacoEditorLoaded
            ? this._cardEditor.getValue()
            : Constants.emptyCardPayload;
    }

    // private getCurrentSampleDataEditorPayload(): string {
    //     return this._isMonacoEditorLoaded && this._sampleDataEditor ? this._sampleDataEditor.getValue() : "";
    // }

    private updateCardFromJson() {
        try {
            this.preventJsonUpdate = true;

            if (!this.preventCardUpdate) {
                this.designerSurface.setCardPayloadAsString(
                    this.getCurrentCardEditorPayload(),
                );
            }
        } finally {
            this.preventJsonUpdate = false;
        }
    }

    private scheduleUpdateCardFromJson() {
        clearTimeout(this.cardUpdateTimer);

        if (!this.preventCardUpdate) {
            this.cardUpdateTimer = setTimeout(() => {
                this.updateCardFromJson();
                AWTLogManager.getLogger().logEvent({
                    name: JSON_EDITOR,
                    priority: AWTEventPriority.Normal,
                });
            }, 100);
        }
    }

    private scheduleLayoutUpdate() {
        clearTimeout(this.updateLayoutTimer);

        this.updateLayoutTimer = setTimeout(() => {
            this.designerSurface.updateLayout(false);
        }, 50);
    }

    private onResize() {
        if (window.innerWidth > 700) {
            document.getElementById('leftPanel').style.display = 'block';
            document.getElementById('rightPanel').style.display = 'block';
        } else {
            document.getElementById('leftPanel').style.display = 'none';
            document.getElementById('rightPanel').style.display = 'none';
        }

        if (window.innerHeight <= 300) {
            this._editorElement.style.grid = `1fr 23px 0% / 1fr 177px 1fr`;
        } else {
            this._editorElement.style.grid = `1fr 37px 0% / 1fr 177px 1fr`;
        }
        this._cardEditor.layout();
    }

    private handleFoldResize() {
        let fold = document.getElementById('fold');
        let designerHost = document.getElementById('designerHost');
        fold.style.width =
            designerHost.getBoundingClientRect().width + 54 + 'px';
    }

    public monacoModuleLoaded(monaco: any = null) {
        if (!monaco) {
            monaco = window['monaco'];
        }

        let monacoConfiguration = {
            schemas: [
                {
                    uri: 'http://adaptivecards.io/schemas/adaptive-card.json',
                    schema: AdaptiveCardSchema,
                    fileMatch: ['*'],
                },
            ],
            validate: true,
            allowComments: true,
        };

        // TODO: set this in our editor instead of defaults
        monaco.languages.json.jsonDefaults.setDiagnosticsOptions(
            monacoConfiguration,
        );

        this._cardEditor = monaco.editor.create(this._jsonEditorElement, {
            folding: true,
            fontSize: 13.5,
            language: 'json',
            minimap: {
                enabled: false,
            },
            automaticLayout: true,
            formatOnPaste: true,
            scrollbar: {
                horizontalScrollbarSize: 5,
                verticalScrollbarSize: 5,
            },
        });

        this._cardEditor.onDidChangeModelContent(() => {
            this.scheduleUpdateCardFromJson();
        });

        window.addEventListener('resize', this.onResize);

        this._isMonacoEditorLoaded = true;

        this.updateJsonEditorsLayout();
        this.updateJsonFromCard(false);
    }

    private handleCopyJson = (e: KeyboardEvent) => {
        if (e.key === 'c' || e.key === 'x') {
            if (e.ctrlKey || e.metaKey) {
                AWTLogManager.getLogger().logEvent({
                    name: JSON_COPIED,
                    priority: AWTEventPriority.Normal,
                });
            }
        }
    };

    private handleKeyDownOnJSONEditor = (event: KeyboardEvent) => {
        if (event.key === ' ' || event.key === 'Enter') {
            this.isJsonEditorVisible = !this.isJsonEditorVisible;
        } else if (!event.shiftKey && event.key === 'Tab') {
            const propertyHelpIcon = document.querySelector(
                '#propertyEditorHeader .editorHelpIcon',
            );
            if (propertyHelpIcon) {
                (propertyHelpIcon as HTMLDivElement).focus();
                event.preventDefault();
            }
        }
    };

    private navigateToErrorLine() {
        let errors = [];
        for (let i = 0; i < monaco.editor.getModelMarkers({}).length; i++) {
            if (
                monaco.editor.getModelMarkers({})[i].severity ===
                monaco.MarkerSeverity.Error
            )
                errors.push(monaco.editor.getModelMarkers({})[i]);
        }
        if (errors.length > 0) {
            const lineNumber = errors[0].startLineNumber;
            const columnNumber = errors[0].startColumn;
            const selection = new monaco.Selection(
                lineNumber,
                columnNumber,
                lineNumber,
                columnNumber,
            );
            this._cardEditor.setSelection(selection);
            this._cardEditor.focus();
            this._cardEditor.revealLine(lineNumber);
        }
    }

    private addToUndoStack(payload: string) {
        let doAdd: boolean = true;

        if (this._undoStack.length > 0) {
            doAdd = this._undoStack[this._undoStack.length - 1] !== payload;
        }

        if (doAdd) {
            let undoPayloadsToDiscard =
                this._undoStack.length - (this._undoStackIndex + 1);

            if (undoPayloadsToDiscard > 0) {
                this._undoStack.splice(
                    this._undoStackIndex + 1,
                    undoPayloadsToDiscard,
                );
            }

            this._undoStack.push(payload);

            if (this._undoStack.length > CardDesigner.MAX_UNDO_STACK_SIZE) {
                this._undoStack.splice(0, 1);
            }

            this._undoStackIndex = this._undoStack.length - 1;
        }
    }

    private handlePointerUp() {
        this.endDrag();
        this.designerSurface.handlePointerUp();
    }

    private handlePointerMove(e: PointerEvent) {
        this._currentMousePosition = { x: e.x, y: e.y };

        const isPointerOverDesigner = this.designerSurface.isPointerOver(
            this._currentMousePosition.x,
            this._currentMousePosition.y,
        );

        this._designerSurface.handlePointerMove(e);

        let peerDropped = false;

        if (this._draggedInventoryItem && isPointerOverDesigner) {
            let peer = this._draggedInventoryItem.createPeer(
                this.designerSurface,
            );

            let clientCoordinates =
                this.designerSurface.pageToClientCoordinates(
                    this._currentMousePosition.x,
                    this._currentMousePosition.y,
                );

            if (this.designerSurface.tryDrop(clientCoordinates, peer)) {
                this.endDrag();
                this.designerSurface.startDrag(
                    this.designerSurface.peerToInsert,
                    null,
                    true,
                );

                peerDropped = true;
            }
        }

        if (!peerDropped && this._draggedElement) {
            this._draggedElement.style.left =
                this._currentMousePosition.x - 10 + 'px';
            this._draggedElement.style.top =
                this._currentMousePosition.y - 10 + 'px';
        }
    }

    private handleKeyDown(e: KeyboardEvent) {
        let handled = false;
        switch (e.key) {
            case 'Escape':
                handled = this.handleEscape();
                if (!handled) {
                    handled = this._treeViewSurface.handleEscape();
                }
                if (!handled) {
                    handled = this.designerSurface.handleEscape();
                }
                if (!handled && window.innerWidth <= 700) {
                    this.showEditorPaneOnMobile();
                }
                break;
        }
        if (handled) {
            e.stopPropagation();
        }
    }

    private handleEscape(): boolean {
        if (this._draggedInventoryItem) {
            this.endDrag();
            return true;
        }
        return false;
    }

    private setJsonEditorSize(editorSize: string) {
        if (window.innerHeight <= 300) {
            this._editorElement.style.grid = `1fr 23px ${editorSize} / 1fr 177px 1fr`;
        } else {
            this._editorElement.style.grid = `1fr 37px ${editorSize} / 1fr 177px 1fr`;
        }
    }

    private resetUndoStack() {
        this._undoStack = [];
        this._undoStackIndex = -1;
    }

    private showVisualTreeEditorOnMobile = () => {
        document.getElementById('leftPanel').style.display = 'block';
        document.getElementById('rightPanel').style.display = 'none';
        this.setJsonEditorSize('0%');
    };

    private showEditorPaneOnMobile = () => {
        document.getElementById('leftPanel').style.display = 'none';
        document.getElementById('rightPanel').style.display = 'none';
    };

    private showPropertyEditorOnMobile = () => {
        document.getElementById('leftPanel').style.display = 'none';
        document.getElementById('rightPanel').style.display = 'block';
        this.setJsonEditorSize('0%');
    };

    sampleCatalogueUrl: string = undefined;

    constructor(hostContainer: HostContainer) {
        this._hostContainer = hostContainer;
        this.handlePointerMove = this.handlePointerMove.bind(this);
        this.handlePointerUp = this.handlePointerUp.bind(this);
        this.scheduleLayoutUpdate = this.scheduleLayoutUpdate.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.onResize = this.onResize.bind(this);
    }

    attachTo(root: HTMLElement) {
        root.style.overflow = 'hidden';

        root.innerHTML = `<div id='designerContent' class="designerContent">
                            <div id="leftPanel" class="leftPanel">
                                <div id="treeViewWrapper" class="treeViewWrapper">
                                    <div id="visualTreeEditorHeader"></div>
                                    <div id="treeView" class="treeView"></div>
                                </div>
                            </div>
                            <div id="editorWrapper" class="editorWrapper">
                                <div id="errorPane" class="errorPane"></div>
                                <div id="editor" class="editor">
                                    <div id="editorCanvas" class="editorCanvas">
                                        <div id="designerHost"></div>
                                    </div>
                                    <div class="jsonEditorHeaderBG"></div>
                                    <div id="jsonEditorHeader" class="jsonEditorHeader">
                                    </div>
                                    <div id="jsonEditorWrapper" class="jsonEditorWrapper">
                                    <div id="jsonEditorSplitter" class="jsonEditorSplitter"></div>
                                    <div id="jsonEditor" class="jsonEditor"></div>
                                    </div>
                                </div>
                            </div>
                            <div id="rightPanel" class="rightPanel">
                                <div id="propertySheetWrapper" class="propertySheetWrapper">
                                    <div id="propertyEditorHeader"></div>
                                    <div id="propertyEditorHeaderType" class="propertyEditorHeaderType"></div>
                                    <div id="propertySheet" class="propertySheet"></div>
                                </div>
                            </div>
                        </div>
                        `;
        const visualTreeEditorHeader = document.getElementById(
            'visualTreeEditorHeader',
        );

        const visualTreeEditorHeaderRoot = createRoot(visualTreeEditorHeader);
        visualTreeEditorHeaderRoot.render(
            React.createElement(EditorHeader, {
                type: EditorHeaderType.VisualTreeEditor,
                onCloseIconClick: this.showEditorPaneOnMobile,
            }),
        );

        const propertyEditorHeader = document.getElementById(
            'propertyEditorHeader',
        );

        const propertyEditorHeaderRoot = createRoot(propertyEditorHeader);
        propertyEditorHeaderRoot.render(
            React.createElement(EditorHeader, {
                type: EditorHeaderType.PropertyEditor,
                onCloseIconClick: this.showEditorPaneOnMobile,
            }),
        );

        this._jsonEditorHeaderElement =
            document.getElementById('jsonEditorHeader');
        const _jsonEditorHeaderElementRoot = createRoot(
            this._jsonEditorHeaderElement,
        );

        _jsonEditorHeaderElementRoot.render(
            React.createElement(CardDesignerEditor, {
                onJsonEditorKeyDown: this.handleKeyDownOnJSONEditor,
                onJsonEditorMouseClick: () => {
                    this.isJsonEditorVisible = !this.isJsonEditorVisible;
                },
                showEditorPaneOnMobile: this.showEditorPaneOnMobile,
                showPropertyEditorOnMobile: this.showPropertyEditorOnMobile,
                showVisualTreeEditorOnMobile: this.showVisualTreeEditorOnMobile,
            }),
        );

        this._jsonEditorElement = document.getElementById('jsonEditor');
        this._jsonEditorElement.addEventListener(
            'keydown',
            this.handleCopyJson,
        );

        this._editorElement = document.getElementById('editor');

        this.setJsonEditorSize('0%');

        this._propertySheetElement = document.getElementById('propertySheet');

        this._designerHostElement = document.getElementById('designerHost');

        const jsonEditorWrapper = document.getElementById('jsonEditorWrapper');
        const jsonEditorSplitter =
            document.getElementById('jsonEditorSplitter');

        const splitter = new Splitter(
            jsonEditorSplitter,
            jsonEditorWrapper,
            false,
            false,
            71,
        );

        splitter.onResizing = (splitter: Splitter, newSize: number) => {
            this.jsonEditorSize = `${newSize}px`;
        };

        window.addEventListener('pointermove', this.handlePointerMove);

        window.addEventListener('pointerup', this.handlePointerUp);

        window.addEventListener('resize', this.scheduleLayoutUpdate);

        document.addEventListener('keydown', this.handleKeyDown);

        const editorCanvas = document.getElementById('editorCanvas');
        editorCanvas.addEventListener('click', (e: MouseEvent) => {
            this.designerSurface.clearSelectedPeer();
            e.preventDefault();
        });

        const designerContent = document.getElementById('designerContent');
        designerContent.addEventListener('click', (e: MouseEvent) => {
            if (
                (e.target as HTMLElement).id === 'designerContent' &&
                window.innerWidth <= 700
            ) {
                this.showEditorPaneOnMobile();
            }
        });

        const treeViewElement = document.getElementById('treeView');
        this._treeViewSurface = new TreeViewSurface(treeViewElement);

        // this.recreateDesignerSurface();

        AdaptiveCard.enableFullJsonRoundTrip = true;
        AdaptiveCard.useMarkdownInRadioButtonAndCheckbox = true;
        AdaptiveCard.useAdvancedCardBottomTruncation = false;
        AdaptiveCard.useAdvancedTextBlockTruncation = true;

        AdaptiveCard.onProcessMarkdown = (
            text: string,
            result: Adaptive.IMarkdownProcessingResult,
        ) => {
            CardDesigner.internalProcessMarkdown(text, result);
        };

        const card = new AdaptiveCard();
        card.onImageLoaded = (image: Adaptive.Image) => {
            this.scheduleLayoutUpdate();
        };

        this.card = card;

        let fold: HTMLElement = document.getElementById('fold');
        if (fold) {
            window.addEventListener('resize', this.handleFoldResize);
        }
    }

    detach() {
        window.removeEventListener('pointermove', this.handlePointerMove);

        window.removeEventListener('pointerup', this.handlePointerUp);

        window.removeEventListener('resize', this.scheduleLayoutUpdate);

        document.removeEventListener('keydown', this.handleKeyDown);

        window.removeEventListener('resize', this.onResize);

        this._treeViewSurface.detach();

        let fold: HTMLElement = document.getElementById('fold');
        if (fold) {
            window.removeEventListener('resize', this.handleFoldResize);
        }
    }

    undo() {
        if (this.canUndo) {
            this._undoStackIndex--;

            let card = this._undoStack[this._undoStackIndex];

            this.setCardPayload(JSON.parse(card));
        }
    }

    redo() {
        if (this.canRedo) {
            this._undoStackIndex++;

            let card = this._undoStack[this._undoStackIndex];

            this.setCardPayload(JSON.parse(card));
        }
    }

    setCard(payload: object) {
        try {
            this.preventJsonUpdate = true;
            this.resetUndoStack();

            if (!this.preventCardUpdate) {
                this.designerSurface.setCardPayloadAsObject(payload);
            }
        } finally {
            this.preventJsonUpdate = false;
        }

        this.updateJsonFromCard();
    }

    getCard(): object {
        return this.designerSurface.card.toJSON();
    }

    get currentTargetVersion(): Shared.TargetVersion {
        return Shared.Versions.latest;
    }

    get canUndo(): boolean {
        return this._undoStackIndex >= 1;
    }

    get canRedo(): boolean {
        return this._undoStackIndex < this._undoStack.length - 1;
    }

    get card(): AdaptiveCard {
        return this._card;
    }

    set card(value: AdaptiveCard) {
        if (this._card !== value) {
            this._card = value;

            if (this._card) {
                this._card.designMode = true;
                this._card.hostConfig = this._hostContainer.getHostConfig();
            }

            this.recreateDesignerSurface();
            this.updateCardFromJson();
        }
    }

    get designerSurface(): CardDesignerSurface {
        return this._designerSurface;
    }

    get draggeElement(): HTMLElement {
        return this._draggedElement;
    }

    set draggedElement(element: HTMLElement) {
        this._draggedElement = element;
    }

    get isJsonEditorVisible(): boolean {
        return this._isJsonEditorVisible;
    }

    set isJsonEditorVisible(visibility: boolean) {
        this._isJsonEditorVisible = visibility;
        const jsonHeaderChevron = this._editorElement.querySelector(
            '.jsonEditorHeaderChevron',
        );
        if (this._isJsonEditorVisible) {
            jsonHeaderChevron.classList.add('opened');
            jsonHeaderChevron.setAttribute('aria-label', 'Expanded');
            this.setJsonEditorSize(this.jsonEditorSize);
            this._designerSurface.clearSelectedPeer();
        } else {
            jsonHeaderChevron.classList.remove('opened');
            jsonHeaderChevron.setAttribute('aria-label', 'Collapsed');
            this.setJsonEditorSize('0%');
        }
    }

    get jsonEditorSize(): string {
        return this._jsonEditorSize;
    }

    set jsonEditorSize(value: string) {
        this._jsonEditorSize = value;
        this.setJsonEditorSize(value);
    }

    get hostContainer(): HostContainer {
        return this._hostContainer;
    }
}
